import { useTranslation } from "react-i18next";

import type { LeadV2 } from "../../../api/generated";
import type { OverviewPageAnalytics } from "../../types";

import { LeadV2Status } from "../../../api/generated";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { ArrowRightIcon } from "../../../Icons";
import { Typo } from "../../../Typo";
import { formatEpochToLocaleDate } from "../../../utils/date";
import { useDebounceFn } from "../../../utils/debounce";
import { useOverviewAnalytics } from "../../OverviewAnalyticsProvider";
import { RecentLeadStatus, RecentLeadStatusV2 } from "../../RecentLeadStatus";
import { useRecentLeadsTranslatedContent } from "./useRecentLeadsTranslatedContent";

export type RecentLeadsTableForOverviewProps = {
  leads: LeadV2[] | [];
  redactedFields?: string[];
  isMobile?: boolean;
  isBackoffice?: boolean;
  scaId?: string;
  analytics?: OverviewPageAnalytics;
};

export type RecentLeadsTablesTranslatedContent = {
  leadStatusMapping: Map<string, string>;
  descriptionStatusMapping: Map<LeadV2Status, string>;
  goToLeads: string;
  header: string;
  clientIdColumn: string;
  nameColumn: string;
  createdColumn: string;
  statusColumn: string;
  noDataFound: string;
};

export const RecentLeadsTable = ({
  leads,
  redactedFields = [],
  isMobile = false,
  isBackoffice = false,
  scaId,
}: RecentLeadsTableForOverviewProps) => {
  const { analytics } = useOverviewAnalytics();

  const translatedContent = useRecentLeadsTranslatedContent();
  const { i18n } = useTranslation();
  const { isRecentLeadsStatusV2 } = useFeatureFlags();

  const getStatusLabel = (status: LeadV2Status, rawStatus: string) => {
    if (isRecentLeadsStatusV2) {
      return (
        translatedContent.leadStatusMapping.get(rawStatus) ||
        translatedContent.leadStatusMapping.get(LeadV2Status.Unknown) ||
        ""
      );
    } else {
      return (
        translatedContent.leadStatusMapping.get(status) ||
        translatedContent.leadStatusMapping.get(LeadV2Status.Unknown) ||
        ""
      );
    }
  };

  const translatedLeads = leads.map((lead) => {
    return {
      ...lead,
      tStatus: getStatusLabel(lead.status, lead.rawStatus),
    };
  });

  const formatDate = (epochMillis: number) => {
    if (epochMillis < 0) return "-";
    return formatEpochToLocaleDate(epochMillis, i18n.language).toUpperCase();
  };

  const showButton = translatedLeads.length > 0;

  const columns: {
    field: keyof LeadV2;
    isRedacted?: boolean;
    title: string;
  }[] = [
    {
      field: "clientId",
      isRedacted: redactedFields.includes("clientId"),
      title:
        translatedContent.clientIdColumn +
        (redactedFields.includes("clientId") ? " (HIDDEN)" : ""),
    },
    {
      field: "name",
      isRedacted: redactedFields?.includes("name"),
      title:
        translatedContent.nameColumn +
        (redactedFields?.includes("name") ? " (HIDDEN)" : ""),
    },
  ];

  const [debounceTooltipTrack] = useDebounceFn(
    (status: string, isMobile: boolean) => {
      analytics?.trackRecentLeadStatusTooltipEvent(status, isMobile);
    },
    2000,
  );

  if (isRecentLeadsStatusV2) {
    return (
      <div className="flex min-w-[300px] flex-col overflow-hidden rounded-2xl border border-stroke-primary bg-surface-base-background shadow-sm lg:min-w-[500px] lg:flex-1">
        <div className="flex items-center justify-between p-6 pb-2 lg:p-6">
          <Typo
            element="h1"
            type={isMobile ? "labelMD" : "labelLG"}
            textColor="text-text-primary"
          >
            {translatedContent.header}
          </Typo>
          {showButton ? (
            <Typo
              element="span"
              type="labelSM"
              smType="labelXS"
              textColor="text-text-primary"
            >
              <a
                href={isBackoffice && scaId ? `/leads/${scaId}` : "/leads"}
                onClick={(event) => analytics?.trackGoToLeadsEvent(event)}
                className="flex items-center gap-1"
              >
                {translatedContent.goToLeads}
                {isMobile && (
                  <ArrowRightIcon className="inline-block h-[12px] w-[12px] text-icon-primary" />
                )}
              </a>
            </Typo>
          ) : null}
        </div>

        <div className="overflow-hidden overflow-x-auto">
          <table className={`w-full ${isMobile ? "min-w-max" : ""}`}>
            <thead>
              <tr className="border-b border-stroke-primary">
                {isBackoffice ? (
                  <>
                    {columns.map((column) => (
                      <Typo
                        key={column?.field}
                        element="th"
                        type="captionSM"
                        textColor="text-text-base-secondary"
                        classes={`pl-6 py-4 text-left ${column?.isRedacted ? "bg-red-100" : ""}`}
                      >
                        {column.title}
                      </Typo>
                    ))}
                  </>
                ) : (
                  <Typo
                    element="th"
                    type="captionSM"
                    textColor="text-text-base-secondary"
                    classes="pl-6 py-4 text-left"
                  >
                    {!redactedFields?.includes("name")
                      ? translatedContent.nameColumn
                      : translatedContent.clientIdColumn}{" "}
                  </Typo>
                )}
                <Typo
                  element="th"
                  type="captionSM"
                  textColor="text-text-base-secondary"
                  classes={`${isMobile ? "text-left" : "text-right"}`}
                >
                  {translatedContent.signupDateColumn}
                </Typo>
                <Typo
                  element="th"
                  type="captionSM"
                  textColor="text-text-base-secondary"
                  classes={`pr-6 py-4 text-left lg:text-right ${isMobile ? "text-left" : "text-right"}`}
                >
                  {translatedContent.onboardingStatusColumn}
                </Typo>
              </tr>
            </thead>
            <tbody>
              {translatedLeads.length === 0 ? (
                <tr>
                  <td
                    className="px-6 py-4 text-center text-gray-500"
                    colSpan={isBackoffice ? columns.length + 2 : 3}
                  >
                    {translatedContent.noDataFound}
                  </td>
                </tr>
              ) : (
                translatedLeads.map((lead, index) => (
                  <tr
                    key={lead.clientId + "-" + index}
                    className={`border-stroke-primary transition-colors hover:bg-gray-50 ${index === translatedLeads.length - 1 ? "" : "border-b"}`}
                  >
                    {isBackoffice ? (
                      <>
                        {columns.map((column) => (
                          <Typo
                            key={column.field}
                            element="td"
                            type={isMobile ? "bodySM" : "captionMD"}
                            textColor="text-text-primary"
                            classes={`pl-6 pr-4 py-4 text-left min-w-[136px] lg:min-w-[220px] truncate ellipsis ${
                              column?.isRedacted ? "bg-red-100" : ""
                            }`}
                          >
                            {lead[column.field]}
                          </Typo>
                        ))}
                      </>
                    ) : (
                      <Typo
                        element="td"
                        type={isMobile ? "bodySM" : "captionMD"}
                        textColor="text-text-primary"
                        classes="pl-6 pr-4 py-4 text-left max-w-[120px] lg:max-w-[227px] truncate"
                      >
                        {!redactedFields?.includes("name")
                          ? lead.name
                          : lead.clientId}
                      </Typo>
                    )}
                    <Typo
                      element="td"
                      type={isMobile ? "bodySM" : "bodyMD"}
                      textColor="text-text-primary"
                      classes={`py-4 min-w-[120px] text-left ${isMobile ? "" : " text-right"}`}
                    >
                      {formatDate(lead.createdAt)}
                    </Typo>
                    <td className="pe-4 text-left">
                      <RecentLeadStatusV2
                        rawStatus={lead.rawStatus}
                        displayValue={lead.tStatus}
                        isMobile={isMobile}
                        onTooltipHover={() =>
                          debounceTooltipTrack(lead.rawStatus, isMobile)
                        }
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Fallback to old status if the flag is off
  return (
    <div className="flex min-w-[300px] flex-col overflow-hidden rounded-2xl border border-stroke-primary bg-surface-base-background shadow-sm lg:min-w-[500px] lg:flex-1">
      <div className="flex items-center justify-between p-6">
        <Typo
          element="h1"
          type={isMobile ? "labelMD" : "labelLG"}
          textColor="text-text-primary"
        >
          {translatedContent.header}
        </Typo>
        {showButton ? (
          <Typo
            element="span"
            type="labelMD"
            smType="labelXS"
            textColor="text-text-primary"
          >
            <a
              href={isBackoffice && scaId ? `/leads/${scaId}` : "/leads"}
              onClick={(event) => analytics?.trackGoToLeadsEvent(event)}
            >
              {translatedContent.goToLeads}
            </a>
          </Typo>
        ) : null}
      </div>

      <div className="overflow-hidden overflow-x-auto">
        <table className={`w-full ${isMobile ? "min-w-max" : ""}`}>
          <thead>
            <tr className="border-b border-stroke-primary">
              {isBackoffice ? (
                <>
                  {columns.map((column) => (
                    <Typo
                      key={column?.field}
                      element="th"
                      type="captionSM"
                      textColor="text-text-secondary"
                      classes={`pl-6 py-4 text-left ${column?.isRedacted ? "bg-red-100" : ""}`}
                    >
                      {column.title}
                    </Typo>
                  ))}
                </>
              ) : (
                <Typo
                  element="th"
                  type="captionSM"
                  textColor="text-text-secondary"
                  classes="pl-6 py-4 text-left"
                >
                  {!redactedFields?.includes("name")
                    ? translatedContent.nameColumn
                    : translatedContent.clientIdColumn}{" "}
                </Typo>
              )}
              <Typo
                element="th"
                type="captionSM"
                textColor="text-text-secondary"
                classes={`${isMobile ? "text-left" : "text-right"}`}
              >
                {translatedContent.createdColumn}
              </Typo>
              <Typo
                element="th"
                type="captionSM"
                textColor="text-text-secondary"
                classes={`pr-6 py-4 text-left md:text-right ${isMobile ? "text-left" : "text-right"}`}
              >
                {translatedContent.statusColumn}
              </Typo>
            </tr>
          </thead>
          <tbody>
            {translatedLeads.length === 0 ? (
              <tr>
                <td className="px-6 py-4 text-center text-gray-500" colSpan={3}>
                  {translatedContent.noDataFound}
                </td>
              </tr>
            ) : (
              translatedLeads.map((lead, index) => (
                <tr
                  key={index}
                  className={`border-stroke-primary transition-colors hover:bg-gray-50 ${index === translatedLeads.length - 1 ? "" : "border-b"}`}
                >
                  {isBackoffice ? (
                    <>
                      {columns.map((column) => (
                        <Typo
                          key={column.field}
                          element="td"
                          type={isMobile ? "bodySM" : "captionMD"}
                          textColor="text-text-primary"
                          classes={`pl-6 pr-4 py-4 text-left min-w-[136px] md:min-w-[220px] truncate ellipsis ${
                            column?.isRedacted ? "bg-red-100" : ""
                          }`}
                        >
                          {lead[column.field]}
                        </Typo>
                      ))}
                    </>
                  ) : (
                    <Typo
                      element="td"
                      type={isMobile ? "bodySM" : "captionMD"}
                      textColor="text-text-primary"
                      classes="pl-6 pr-4 py-4 text-left min-w-[136px] truncate ellipsis"
                    >
                      {!redactedFields?.includes("name")
                        ? lead.name
                        : lead.clientId}
                    </Typo>
                  )}
                  <Typo
                    element="td"
                    type={isMobile ? "bodySM" : "captionMD"}
                    textColor="text-text-primary"
                    classes={`py-4 min-w-[136px] text-left ${isMobile ? "" : " text-right"}`}
                  >
                    {formatDate(lead.createdAt)}
                  </Typo>
                  <td className="pe-4 text-left">
                    <RecentLeadStatus
                      status={lead.status}
                      displayValue={lead.tStatus}
                      isMobile={isMobile}
                      descriptionMapping={
                        translatedContent.descriptionStatusMapping
                      }
                      onTooltipHover={() =>
                        debounceTooltipTrack(lead.status, isMobile)
                      }
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
